import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Lock, Eye, EyeOff, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const checks = [
  { id: 'len', label: 'At least 8 characters', re: /.{8,}/ },
  { id: 'upper', label: 'Uppercase letter', re: /[A-Z]/ },
  { id: 'lower', label: 'Lowercase letter', re: /[a-z]/ },
  { id: 'digit', label: 'Number', re: /\d/ },
  { id: 'special', label: 'Special character', re: /[^A-Za-z0-9]/ },
];

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) setError('Missing reset token. Please use the link from your email.');
  }, [token]);

  const ok = (re) => re.test(password);
  const allOk = checks.every((c) => ok(c.re)) && password === confirm;

  const submit = async (e) => {
    e.preventDefault();
    if (!allOk) {
      setError('Password does not meet all requirements or does not match the confirmation.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(typeof data.detail === 'string' ? data.detail : 'Could not reset password.');
        return;
      }
      setDone(true);
      toast.success('Password reset successfully!');
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-emerald-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl border-0" data-testid="reset-password-card">
        <CardHeader>
          <CardTitle className="text-2xl">Set a new password</CardTitle>
          <CardDescription>Choose a strong password to secure your account.</CardDescription>
        </CardHeader>
        <CardContent>
          {done ? (
            <div className="text-center py-6" data-testid="reset-done">
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
              <p className="text-slate-800 font-medium mb-2">Password reset!</p>
              <p className="text-sm text-slate-600 mb-4">You can now log in with your new password.</p>
              <Link to="/login"><Button className="rounded-full">Go to Login</Button></Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">New Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type={show ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 h-12"
                    required
                    data-testid="reset-new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShow(!show)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                    data-testid="reset-toggle-show"
                  >
                    {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="space-y-1 text-xs">
                {checks.map((c) => (
                  <div key={c.id} className={`flex items-center gap-2 ${ok(c.re) ? 'text-emerald-600' : 'text-slate-500'}`} data-testid={`req-${c.id}`}>
                    {ok(c.re) ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                    {c.label}
                  </div>
                ))}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Confirm Password</label>
                <Input
                  type={show ? 'text' : 'password'}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="h-12"
                  required
                  data-testid="reset-confirm-password"
                />
                {confirm && confirm !== password && (
                  <p className="text-xs text-red-500">Passwords don't match</p>
                )}
              </div>

              {error && <p className="text-sm text-red-600" data-testid="reset-error">{error}</p>}

              <Button type="submit" disabled={loading || !allOk || !token} className="w-full h-12 rounded-full bg-indigo-600 hover:bg-indigo-700" data-testid="reset-submit">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Reset Password'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
